import { Button, Flex, Tooltip, Typography } from '@aloft/toolkit-sui';

// eslint-disable-next-line aloft/prefer-sound-ui-button
import { useCertifyButton } from '../../../hooks/useCertifyButton.ts';
import { selectIsCommentaryLoaded } from '../../../redux/features/selectors/commentarySelectors/selectIsCommentaryLoaded.ts';
import {
  selectIncludeCostApproach,
  selectOrder,
  selectRequiresCertification,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { getConfig } from '../../../utils/getConfig.ts';
import { useExportModal } from '../../ExportModal/useExportModal.tsx';
import { SUBNAV_HEIGHT } from '../consts.ts';
import { HeaderSubNavItem } from './HeaderSubNavItem.tsx';
import { INSPECTIFY_SUBNAV_GROUPS, SAAS_ONLY_ROUTES, SAAS_SUBNAV_GROUPS } from './constants.ts';
import { NavGroup, SubNavItemType, groupNavItems } from './types.ts';
import { useActiveSubNav } from './useActiveSubNav.ts';

const getGroupTitleColor = (label: string) => {
  switch (label) {
    case 'SALES':
      return '#0072b2';
    case 'RENTALS':
      return '#e79f00';
    default:
      return '#9ca6af';
  }
};

type DesktopSubNavRoute = (typeof INSPECTIFY_SUBNAV_GROUPS)[number]['routes'][number];

const desktopSubNavRoutes = new Set<DesktopSubNavRoute>([
  ...INSPECTIFY_SUBNAV_GROUPS.flatMap((group) => group.routes),
  ...SAAS_SUBNAV_GROUPS.flatMap((group) => group.routes),
]);
const costApproachRoutes = new Set(['cost-approach', 'craftsman-cost-approach']);

const isDesktopSubNavItem = (
  item: SubNavItemType,
): item is SubNavItemType & { route: DesktopSubNavRoute } =>
  desktopSubNavRoutes.has(item.route as DesktopSubNavRoute);

export const HeaderDesktopSubNav = () => {
  const { canCertify, certifyErrorText, handleCertifyClick, certifyModal } = useCertifyButton();
  const subnav = useActiveSubNav();
  const isCommentaryLoaded = useAppSelector((state) => selectIsCommentaryLoaded(state));
  const { modal } = useExportModal(isCommentaryLoaded);
  const order = useAppSelector(selectOrder);
  const requiresCertification = useAppSelector(selectRequiresCertification);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);

  if (!subnav) return null;
  const visibleSubnav = subnav
    .filter((item) => {
      if (SAAS_ONLY_ROUTES.includes(item.route)) {
        return false;
      }
      if (!includeCostApproach && costApproachRoutes.has(item.route)) {
        return false;
      }
      return true;
    })
    .filter(isDesktopSubNavItem);

  let subnavGroups: NavGroup<DesktopSubNavRoute>[] = INSPECTIFY_SUBNAV_GROUPS;

  if (!order.hasRentalConfiguration) {
    subnavGroups = subnavGroups.filter((group) => group.label !== 'RENTALS');
  }

  const groupedSubnav = groupNavItems(
    visibleSubnav as (SubNavItemType & { route: DesktopSubNavRoute })[],
    subnavGroups,
  );

  const handleDocumentsClick = () => {
    if (!order.orderNumber) return;

    const url = `${String(getConfig('INSPECTIFY_MARKETPLACE_BASE_URL'))}/orders/${order.orderNumber}/result`;

    window.open(url, '_blank');
    window.focus();
  };

  const certifyButtonProps = {
    text: requiresCertification ? 'Certify' : 'Complete',
    onClick: handleCertifyClick,
    size: 'sm' as const,
    __testId: 'craftsman-certify-button',
    __dangerDoNotUseSx: {
      opacity: canCertify ? 1 : 0.6,
      cursor: canCertify ? 'pointer' : 'not-allowed',
    },
  };

  return (
    <Flex
      className="main-header-desktop-subnav"
      position="absolute"
      alignItems="stretch"
      top="3rem"
      height={SUBNAV_HEIGHT}
      width="calc(100vw + 3rem + 8px)"
      marginLeft="calc(-3rem - 8px)"
      paddingLeft="3rem"
      zIndex={9}
      backgroundColor="surface1"
    >
      <Flex className="main-header-desktop-subnav-groups" alignItems="center" height="100%">
        {groupedSubnav.map((group, groupIndex) => (
          <Flex
            className="main-header-desktop-subnav-group"
            key={`header-subnav-group-${group.label}`}
            alignItems="center"
            paddingRight={groupIndex < groupedSubnav.length - 1 ? '0.2rem' : '0'}
          >
            <Flex
              className={[
                'main-header-desktop-subnav-group-shell',
                groupIndex === 0 ? 'main-header-desktop-subnav-group-shell-first' : '',
              ].join(' ')}
            >
              <Flex className="main-header-desktop-subnav-group-title-wrap">
                <Typography
                  variant="subtitle2"
                  __dangerDoNotUseSx={{
                    textTransform: 'uppercase',
                    color: getGroupTitleColor(group.label),
                  }}
                >
                  {group.label}
                </Typography>
              </Flex>
              <Flex alignItems="center">
                {group.items.map((item) => (
                  <HeaderSubNavItem key={item.route} item={item} />
                ))}
              </Flex>
            </Flex>
          </Flex>
        ))}
      </Flex>
      {isCommentaryLoaded && (
        <Flex className="main-header-desktop-subnav-actions">
          <>
            {canCertify ? (
              <Button {...certifyButtonProps} />
            ) : (
              <Tooltip label={certifyErrorText ?? 'Certify the report'}>
                <Button {...certifyButtonProps} />
              </Tooltip>
            )}
            <Button text="Documents" size="sm" variant="secondary" onClick={handleDocumentsClick} />
          </>
        </Flex>
      )}
      {certifyModal}
      {modal}
    </Flex>
  );
};
